import React from 'react';
import dayjs from 'dayjs';

import Routes from 'constants/Routes';
import { NOTIFICATIONS_TYPES } from 'constants/NotificationsConstants';
import { isAccountTypeCompany } from 'components/common/Form/AccountTypeSelect/const';
import { getTimezoneLabel } from 'components/common/listConstants';
import { sliceText } from 'helpers/sliceText';

import style from 'components/Notifications/NotificationsList/NotificationsList.module.scss';
import { interviewTypesValues } from 'components/Interview/interviewConstants';
const EMAIL_LENGTH = 30;

export const formatNotificationsDate = (differenceInMinutes, t) => {
    if (differenceInMinutes < 1) {
        return t('notifications:justNow');
    }
    if (differenceInMinutes >= 1 && differenceInMinutes < 60) {
        return t('notifications:minAgo').replace('%min%', Math.round(differenceInMinutes));
    }
    if (differenceInMinutes >= 60 && differenceInMinutes < 60 * 2) {
        return t('notifications:hourAgo');
    }
    if (differenceInMinutes >= 60 * 2 && differenceInMinutes < 60 * 24) {
        return t('notifications:hoursAgo').replace('%hrs%', Math.round(differenceInMinutes / 60));
    }
    if (differenceInMinutes >= 60 * 24 && differenceInMinutes < 60 * 24 * 2) {
        return t('notifications:dayAgo');
    }
    if (differenceInMinutes >= 60 * 24 * 2 && differenceInMinutes < 60 * 24 * 7) {
        return t('notifications:daysAgo').replace('%days%', Math.round(differenceInMinutes / (60 * 24)));
    }
    if (differenceInMinutes >= 60 * 24 * 7 && differenceInMinutes <= 60 * 24 * 7 * 2) {
        return t('notifications:weekAgo');
    }
    return t('notifications:weeksAgo').replace('%weeks%', Math.round(differenceInMinutes / (60 * 24 * 7)));
};

const destructureNotificationData = (data) => {
    const result = {};
    for (const dataEntry of data) {
        result[dataEntry.label] = dataEntry.value;
    }
    return result;
};

const CLIENT_INTERVIEW_NOTIFICATION_TRANSLATION_STRINGS = {
    [NOTIFICATIONS_TYPES.clientInvitationAccept]: 'notifications:invitationAccepted',
    [NOTIFICATIONS_TYPES.clientInvitationDecline]: 'notifications:invitationDecline',
    [NOTIFICATIONS_TYPES.clientNewApplication]: 'notifications:newApplication',
};

export const formatClientInterviewNotification = (type, notificationData, history, t) => {
    const { resourceName, resourceId, interviewTitle, interviewId } = destructureNotificationData(notificationData);
    const resourceProfileLink = Routes.publicProfile.path
        .replace(':interviewId', interviewId)
        .replace(':userId', resourceId);
    const interviewLink = Routes.interviewDetail.path.replace(':id', interviewId);

    return (
        <>
            <span onClick={() => history.push(resourceProfileLink)} className={style.link}>
                {sliceText(resourceName)}
            </span>
            {t(CLIENT_INTERVIEW_NOTIFICATION_TRANSLATION_STRINGS[type])}
            <span onClick={() => history.push(interviewLink)} className={style.link}>
                {interviewTitle}
            </span>
        </>
    );
};

export const formatResourceInterviewCancellationNotification = (type, notificationData, history, t) => {
    const { interviewTitle, interviewId } = destructureNotificationData(notificationData);
    const interviewLink = Routes.interviewDetail.path.replace(':id', interviewId);

    return (
        <>
            {t('notifications:interviewCancelledBeforeLink')}
            <span className={style.link} onClick={() => history.push(interviewLink)}>
                {interviewTitle}
            </span>
            {t('notifications:interviewCancelledAfterLink')}
        </>
    );
};

export const formatResourceNewInvitationNotification = (type, notificationData, history, t) => {
    const { interviewTitle, interviewId } = destructureNotificationData(notificationData);
    const interviewLink = Routes.interviewDetail.path.replace(':id', interviewId);

    return (
        <>
            {t('notifications:newInvitation')}
            <span className={style.link} onClick={() => history.push(interviewLink)}>
                {interviewTitle}
            </span>
        </>
    );
};

export const formatResourceApplicationApprovedNotification = (type, notificationData, history, t) => {
    const { interviewTitle, interviewId } = destructureNotificationData(notificationData);
    const interviewLink = Routes.interviewDetail.path.replace(':id', interviewId);

    return (
        <>
            {t('notifications:applicationApprovedBeforeLink')}
            <span className={style.link} onClick={() => history.push(interviewLink)}>
                {interviewTitle}
            </span>
            {t('notifications:applicationApprovedAfterLink')}
        </>
    );
};

export const formatResourceInterviewTimeNotification = (type, notificationData, history, t) => {
    const { interviewTitle, interviewId, interviewDate } = destructureNotificationData(notificationData);
    const interviewLink = Routes.interviewDetail.path.replace(':id', interviewId);

    const dateToFormat = dayjs(interviewDate);
    const date = dateToFormat.format('DD MMM YYYY');
    const time = dateToFormat.format('HH:mm');

    return (
        <>
            {t('notifications:interviewTimeBeforeLink')}
            <span className={style.link} onClick={() => history.push(interviewLink)}>
                {interviewTitle}
            </span>
            {t('notifications:scheduledFor')}
            {date}
            {t('notifications:at')}
            {time}
        </>
    );
};

export const formatResourceInterviewRescheduledNotification = (type, notificationData, history, t) => {
    const { interviewTitle, interviewId, newDate, interviewType, mergedEndDate } = destructureNotificationData(
        notificationData,
    );
    const interviewLink = Routes.interviewDetail.path.replace(':id', interviewId);

    const isOnlineDiary = interviewType === interviewTypesValues.online;

    const newDateToFormat = dayjs(newDate);
    const endDateToFormat = dayjs(mergedEndDate);
    const newFormattedDate = newDateToFormat.format('DD MMM YYYY');
    const newFormattedEndDate = endDateToFormat.format('DD MMM YYYY');
    const newFormattedTime = newDateToFormat.format('HH:mm');
    const newFormattedEndTime = endDateToFormat.format('HH:mm');

    const getFormattedDate = () => {
        if (isOnlineDiary && mergedEndDate) {
            return `${newFormattedTime} ${newFormattedDate} - ${newFormattedEndTime} ${newFormattedEndDate}`;
        }

        return `${newFormattedDate} ${newFormattedTime}`;
    };

    return (
        <>
            {t('notifications:interviewRescheduledBeforeLink')}
            <span className={style.link} onClick={() => history.push(interviewLink)}>
                {interviewTitle}
            </span>
            {t('notifications:wasRescheduled')}
            {getFormattedDate()}
        </>
    );
};

export const formatResourceInterviewRescheduledMultipleNotification = (type, notificationData, history, t) => {
    const { interviewTitle, interviewId } = destructureNotificationData(notificationData);
    const interviewLink = Routes.interviewDetail.path.replace(':id', interviewId);

    return (
        <>
            {t('notifications:sessionsRescheduledBeginning')}
            <span className={style.link} onClick={() => history.push(interviewLink)}>
                {interviewTitle}
            </span>
            {t('notifications:sessionsRescheduledEnd')}
        </>
    );
};

export const formatInterviewPaidByWireTransferNotification = (type, notificationData, history, t) => {
    const { interviewTitle, interviewId } = destructureNotificationData(notificationData);
    const interviewLink = Routes.interviewDetail.path.replace(':id', interviewId);

    return (
        <>
            {t('notifications:interviewPaidByWireTransferBeginning')}
            <span className={style.link} onClick={() => history.push(interviewLink)}>
                {interviewTitle}
            </span>
            <br />
            {t('notifications:interviewPaidByWireTransferEnd')}
        </>
    );
};

export const formatResourceMoneyReleaseNotification = (type, notificationData, history, t) => {
    const { interviewTitle, interviewId } = destructureNotificationData(notificationData);
    const interviewLink = Routes.interviewDetail.path.replace(':id', interviewId);
    const paymentSettingsLink = `${Routes.profileEdit.path}?tab=payment`;

    return (
        <>
            {t('notifications:moneyReleaseBeforeLink')}
            <span className={style.link} onClick={() => history.push(interviewLink)}>
                {interviewTitle}
            </span>
            {t('notifications:wasTransferred')}
            <span className={style.link} onClick={() => history.push(paymentSettingsLink)}>
                {t('notifications:wallet')}
            </span>
        </>
    );
};

export const formatResourceDisputeOpenedNotification = (type, notificationData, history, t) => {
    const { clientName, disputeId, interviewTitle, interviewId } = destructureNotificationData(notificationData);
    const interviewLink = Routes.interviewDetail.path.replace(':id', interviewId);
    const disputeLink = `${Routes.disputes.path}?id=${disputeId}`;

    return (
        <>
            {sliceText(clientName)}
            {t('notifications:disputeOpened')}
            <span className={style.link} onClick={() => history.push(disputeLink)}>
                {t('notifications:dispute')}
            </span>
            {t('notifications:forTheInterview')}
            <span className={style.link} onClick={() => history.push(interviewLink)}>
                {interviewTitle}
            </span>
        </>
    );
};

export const formatClientDisputeResponseNotification = (type, notificationData, history, t) => {
    const { resourceName, disputeId, interviewTitle, interviewId } = destructureNotificationData(notificationData);
    const interviewLink = Routes.interviewDetail.path.replace(':id', interviewId);
    const disputeLink = `${Routes.disputes.path}?id=${disputeId}`;

    return (
        <>
            {sliceText(resourceName)}
            {t('notifications:repliedForDispute')}
            <span className={style.link} onClick={() => history.push(disputeLink)}>
                {t('notifications:dispute')}
            </span>
            {t('notifications:forInterview')}
            <span className={style.link} onClick={() => history.push(interviewLink)}>
                {interviewTitle}
            </span>
        </>
    );
};

export const formatResourceDisputeResponseNotification = (type, notificationData, history, t) => {
    const { clientName, disputeId, interviewTitle, interviewId } = destructureNotificationData(notificationData);
    const interviewLink = Routes.interviewDetail.path.replace(':id', interviewId);
    const disputeLink = `${Routes.disputes.path}?id=${disputeId}`;

    return (
        <>
            {sliceText(clientName)}
            {t('notifications:repliedForDispute')}
            <span className={style.link} onClick={() => history.push(disputeLink)}>
                {t('notifications:dispute')}
            </span>
            {t('notifications:forInterview')}
            <span className={style.link} onClick={() => history.push(interviewLink)}>
                {interviewTitle}
            </span>
        </>
    );
};

export const formatDisputeResolutionNotification = (type, notificationData, history, t) => {
    const { interviewTitle, interviewId, disputeId } = destructureNotificationData(notificationData);
    const interviewLink = Routes.interviewDetail.path.replace(':id', interviewId);
    const disputeLink = `${Routes.disputes.path}?id=${disputeId}`;

    return (
        <>
            {t('notifications:your')}
            <span className={style.link} onClick={() => history.push(disputeLink)}>
                {t('notifications:dispute')}
            </span>
            {t('notifications:forInterview')}
            <span className={style.link} onClick={() => history.push(interviewLink)}>
                {interviewTitle}
            </span>
            {t('notifications:hasBeenResolved')}
        </>
    );
};

export const formatInterviewFiredNotification = (type, notificationData, history, t) => {
    const { interviewTitle, interviewId } = destructureNotificationData(notificationData);
    const interviewLink = Routes.interviewDetail.path.replace(':id', interviewId);

    return (
        <>
            {t('notifications:interviewRemoval')}
            <span className={style.link} onClick={() => history.push(interviewLink)}>
                {interviewTitle}
            </span>
        </>
    );
};

export const formatReportUploadedNotification = (type, notificationData, history, t) => {
    const { resourceName, resourceId, interviewTitle, interviewId } = destructureNotificationData(notificationData);
    const interviewLink = Routes.interviewDetail.path.replace(':id', interviewId);
    const resourceProfileLink = Routes.publicProfile.path
        .replace(':userId', resourceId)
        .replace(':interviewId', interviewId);

    return (
        <>
            {t('roles.moderator')}{' '}
            <span onClick={() => history.push(resourceProfileLink)} className={style.link}>
                {sliceText(resourceName)}
            </span>{' '}
            {t('notifications:reportUploaded')}{' '}
            <span onClick={() => history.push(interviewLink)} className={style.link}>
                {interviewTitle}
            </span>
            . {t('notifications:reportUploadedMoneyRelease')}
        </>
    );
};

export const formatHomeworkUploadedNotification = (type, notificationData, history, t) => {
    const { resourceName, resourceId, interviewTitle, interviewId } = destructureNotificationData(notificationData);
    const interviewLink = Routes.interviewDetail.path.replace(':id', interviewId);
    const resourceProfileLink = Routes.publicProfile.path
        .replace(':userId', resourceId)
        .replace(':interviewId', interviewId);

    return (
        <>
            {t('roles.respondent')}{' '}
            <span onClick={() => history.push(resourceProfileLink)} className={style.link}>
                {sliceText(resourceName)}
            </span>{' '}
            {t('notifications:homeworkUploaded')}{' '}
            <span onClick={() => history.push(interviewLink)} className={style.link}>
                {interviewTitle}
            </span>
        </>
    );
};

export const formatHomeworkReminder = (type, notificationData, history, t) => {
    const { hoursLeft, interviewTitle, interviewId } = destructureNotificationData(notificationData);
    const interviewLink = Routes.interviewDetail.path.replace(':id', interviewId);

    return (
        <>
            {t('notifications:homeworkReminder').replace('%hours%', hoursLeft)}{' '}
            <span onClick={() => history.push(interviewLink)} className={style.link}>
                {interviewTitle}
            </span>
        </>
    );
};

export const formatOutdatedKycNotification = (type, notificationData, history, t) => {
    const { accountType } = destructureNotificationData(notificationData);
    if (isAccountTypeCompany(accountType))
        return (
            <>
                {t('notifications:kycExpiredBusiness.start')}
                <span className={style.link} onClick={() => history.push(`${Routes.profileEdit.path}?tab=payment`)}>
                    {t('notifications:kycExpiredBusiness.ref')}
                </span>
                {t('notifications:kycExpiredBusiness.end')}
            </>
        );

    return (
        <>
            {t('notifications:kycExpired.start')}
            <span className={style.link} onClick={() => history.push(`${Routes.profileEdit.path}?tab=payment`)}>
                {t('notifications:kycExpired.ref')}
            </span>
            {t('notifications:kycExpired.end')}
        </>
    );
};

export const formatSheduledEmailNotification = (type, notificationData, history, t) => {
    const { interviewTitle, sessionTitle, date, timezone, interviewId } = destructureNotificationData(notificationData);

    const dateToFormat = dayjs(date).subtract(1, 'hour');
    const dateLabel = dateToFormat.format('DD MMM, YYYY · HH:mm');

    const timezoneLabel = getTimezoneLabel(timezone).split(' ')[0];
    const interviewLink = Routes.interviewDetail.path.replace(':id', interviewId);

    const link = (
        <span onClick={() => history.push(interviewLink)} className={style.link}>
            {`${interviewTitle}`}
        </span>
    );

    const secondPartText = t('notifications:sheduledEmailNotification.end')
        .replace('%sessionTitle%', `"${sessionTitle}"`)
        .replace('%date%', dateLabel)
        .replace('%timezone%', timezoneLabel);

    return (
        <>
            <span>{t('notifications:sheduledEmailNotification.start')}</span>
            {link}
            <span>{secondPartText}</span>
        </>
    );
};

export const formatRecordingReadyNotification = (type, notificationData, history, t) => {
    const { interviewTitle, interviewId, sessionDate } = destructureNotificationData(notificationData);

    const interviewLink = Routes.interviewDetail.path.replace(':id', interviewId) + '#results';
    const date = dayjs(sessionDate).format('DD MMM YYYY');

    const getText = () => {
        switch (type) {
            case NOTIFICATIONS_TYPES.recordingReady:
                return t('interview:orderLabels.recording');
            case NOTIFICATIONS_TYPES.machineTransriptReady:
                return t('interview:orderLabels.machineTranscripts');
            case NOTIFICATIONS_TYPES.humanTranscriptsReady:
                return t('interview:orderLabels.humanTranscripts');
            default:
                return '';
        }
    };

    const link = (
        <span onClick={() => history.push(interviewLink)} className={style.link}>
            {getText()}
        </span>
    );

    const text = (
        <span>
            {t('notifications:recordingReady')
                .replace('{interviewTitle}', interviewTitle)
                .replace('{sessionDate}', date)}
        </span>
    );

    return (
        <>
            {link}
            {text}
        </>
    );
};

export const formatInvitationSentNotification = (type, notificationData, history, t) => {
    const { email } = destructureNotificationData(notificationData);
    const emailText = email ? email : t('notifications:emails');

    return (
        <>
            <div>
                {t('notifications:invitationSent') + ' '}{' '}
                <span className={style.bold}>{sliceText(emailText, 35)} </span>
                {' ' + t('notifications:invitationSentEmail')}
            </div>

            <div className={style.delimiter}> {t('notifications:invitationSentFinish')}</div>
            <div className={style.delimiter}>{t('notifications:invitationCanResend')}</div>
        </>
    );
};

export const formatMemberDeletedNotification = (type, notificationData, history, t) => {
    const { email } = destructureNotificationData(notificationData);

    return (
        <>
            {t('notifications:teamMember') + ' '} <span className={style.bold}>{sliceText(email) + ' '} </span>
            {t('notifications:teamMemberDeleted')}
        </>
    );
};

export const formatRoleDeletionSuccessNotification = (type, notificationData, history, t) => {
    const { role } = destructureNotificationData(notificationData);

    return (
        <>
            <span className={style.bold}>{`Role is deleted`}</span>
            <div>{`Role "${role}" is deleted from the list`}</div>
        </>
    );
};

export const formatRoleDeletionFailedNotification = (type, notificationData, history, t) => {
    return (
        <>
            <span className={style.bold}>{`Role can't be deleted`}</span>
            <div>{'Make sure there are no team members assigned to this role and try again'}</div>
        </>
    );
};

export const formatCancelInvitationNotification = (type, notificationData, history, t) => {
    const { email } = destructureNotificationData(notificationData);

    return (
        <>
            <div>
                Invitation link sent to <span className={style.bold}>{sliceText(email, EMAIL_LENGTH)}</span> has been
                expired. The user has been deleted from the list
            </div>
        </>
    );
};

export const formatRoleChangedNotification = (type, notificationData, history, t) => {
    const { email, role } = destructureNotificationData(notificationData);

    return (
        <>
            <div>
                <span className={style.bold}>{sliceText(email, EMAIL_LENGTH)}</span> set to role{' '}
                <span className={style.bold}>{sliceText(role, EMAIL_LENGTH)}</span>
            </div>
        </>
    );
};

export const formatPermissionNotAllowedNotification = (type, notificationData, history, t) => {
    const { text } = destructureNotificationData(notificationData);

    return (
        <>
            <div>{text}</div>
        </>
    );
};

export const formatConstantReloadNotification = (type, notificationData, history, t) => {
    const { text } = destructureNotificationData(notificationData);
    return (
        <>
            <div>{text}</div>
        </>
    );
};

// firstName: senderUserJson.firstName,
// lastName: senderUserJson.lastName,
// projectId,
// contactId: userId,
// text: messageDTO.text,

export const formatNewMessageNotification = (type, notificationData, history, t) => {
    const { firstName, lastName, projectId, contactId, text } = destructureNotificationData(notificationData);
    const toChat = () => {
        history.push(`${Routes.chat.path}?projectId=${projectId}&contactId=${contactId}`);
    };

    return (
        <>
            <div className={style.newMessage} onClick={toChat}>
                <span>{t('notifications:newMessage') + ` ${firstName} ${lastName}: `}</span>
                <div>{sliceText(text, 100)}</div>
            </div>
        </>
    );
};

export const NOTIFICATIONS_FORMATTER = {
    [NOTIFICATIONS_TYPES.clientInvitationAccept]: formatClientInterviewNotification,
    [NOTIFICATIONS_TYPES.clientNewApplication]: formatClientInterviewNotification,
    [NOTIFICATIONS_TYPES.clientInvitationDecline]: formatClientInterviewNotification,
    [NOTIFICATIONS_TYPES.resourceInterviewCancellation]: formatResourceInterviewCancellationNotification,
    [NOTIFICATIONS_TYPES.resourceNewInvitation]: formatResourceNewInvitationNotification,
    [NOTIFICATIONS_TYPES.resourceApplicationApprove]: formatResourceApplicationApprovedNotification,
    [NOTIFICATIONS_TYPES.resourceInterviewInOneDay]: formatResourceInterviewTimeNotification,
    [NOTIFICATIONS_TYPES.resourceInterviewInThreeDays]: formatResourceInterviewTimeNotification,
    [NOTIFICATIONS_TYPES.resourceInterviewRescheduled]: formatResourceInterviewRescheduledNotification,
    [NOTIFICATIONS_TYPES.resourceMoneyRelease]: formatResourceMoneyReleaseNotification,
    [NOTIFICATIONS_TYPES.resourceOpenedDispute]: formatResourceDisputeOpenedNotification,
    [NOTIFICATIONS_TYPES.resourceDisputeResponse]: formatResourceDisputeResponseNotification,
    [NOTIFICATIONS_TYPES.clientDisputeResponse]: formatClientDisputeResponseNotification,
    [NOTIFICATIONS_TYPES.disputeResolution]: formatDisputeResolutionNotification,
    [NOTIFICATIONS_TYPES.resourceInterviewFire]: formatInterviewFiredNotification,
    [NOTIFICATIONS_TYPES.clientReportUploaded]: formatReportUploadedNotification,
    [NOTIFICATIONS_TYPES.resourceInterviewRescheduledMultiple]: formatResourceInterviewRescheduledMultipleNotification,
    [NOTIFICATIONS_TYPES.resourceHomeworkReminder]: formatHomeworkReminder,
    [NOTIFICATIONS_TYPES.clientHomeworkUploaded]: formatHomeworkUploadedNotification,
    [NOTIFICATIONS_TYPES.outdatedKycStatus]: formatOutdatedKycNotification,
    [NOTIFICATIONS_TYPES.sheduledEmail]: formatSheduledEmailNotification,
    [NOTIFICATIONS_TYPES.humanTranscriptsReady]: formatRecordingReadyNotification,
    [NOTIFICATIONS_TYPES.recordingReady]: formatRecordingReadyNotification,
    [NOTIFICATIONS_TYPES.machineTransriptReady]: formatRecordingReadyNotification,
    [NOTIFICATIONS_TYPES.invitationSent]: formatInvitationSentNotification,
    [NOTIFICATIONS_TYPES.memberDeleted]: formatMemberDeletedNotification,
    [NOTIFICATIONS_TYPES.roleDeletionSuccess]: formatRoleDeletionSuccessNotification,
    [NOTIFICATIONS_TYPES.roleDeletionFailed]: formatRoleDeletionFailedNotification,
    [NOTIFICATIONS_TYPES.cancelInvitation]: formatCancelInvitationNotification,
    [NOTIFICATIONS_TYPES.roleChanged]: formatRoleChangedNotification,
    [NOTIFICATIONS_TYPES.permissionNotAllowed]: formatPermissionNotAllowedNotification,
    [NOTIFICATIONS_TYPES.constantReload]: formatConstantReloadNotification,
    [NOTIFICATIONS_TYPES.newMessage]: formatNewMessageNotification,
    [NOTIFICATIONS_TYPES.interviewPaidByWireTransfer]: formatInterviewPaidByWireTransferNotification,
};
